'use strict';

define('vb/private/vx/registryConfig',[
  'vbc/private/utils',
  'vb/private/constants',
  'vb/private/vx/endpointExtensionAdapter',
  'vb/private/vx/remoteExtensionAdapter',
  'vb/private/vx/extensionRegistry',
  'vb/private/vx/fullDigestExtensionRegistry',
  'vb/private/vx/partialDigestExtensionRegistry',
], (Utils, Constants, EndpointExtensionAdapter, RemoteExtensionAdapter, ExtensionRegistry,
  FullDigestExtensionRegistry, PartialDigestExtensionRegistry) => {
  /**
   * A config object with the class name for the extension adapter to use
   * as well as the registry URL and the extension manager
   */
  class RegistryConfig {
    /**
     * @param {Object} vbInitConfig
     * @param {String} [digestState = 'partial'] - whether to fetch the full or partial digest.
     */
    constructor(vbInitConfig, digestState = Constants.DigestState.PARTIAL) {
      this.localAdapter = null;
      /**
       * Registry digest adapter used to fetch full digests, partial digests, and digest cache URLs.
       * This is set to an endpoint, remote, or local adapter when registry configuration is present.
       * @type {EndpointExtensionAdapter|RemoteExtensionAdapter|null}
       */
      this.digestLoader = null;

      this.fetchFullManifestArray = null;
      this.fetchPartialManifestPromise = null;
      this.loadPartialManifest = null;
      this.loadFullManifest = null;
      /**
       * URL used for the bootstrap partial digest request. Later App UI partial digest URLs are derived from this
       * URL by changing only its appRoute query parameter; the configured full-digest endpoint is not involved.
       * @type {String|null}
       */
      this.partialDigestUrl = null;
      // Optional specialized registry constructor for consumers such as VBSU.
      this.extensionRegistryModule = null;
      /**
       * App UI URL id for the initial partial digest loaded from vbInitConfig.DIGEST_PROMISE.
       * This is the route alias from the browser URL, not necessarily the canonical App UI id used
       * by navigation. It is used for partial digest caching and for seeding the partial registry cache.
       * @type {String|undefined}
       */
      this.partialDigestAppUi = undefined;

      this.digestState = digestState;

      // This is when using the single AppUi digest. It depends on a fetch promise already defined
      // in index.html.
      if (digestState === Constants.DigestState.PARTIAL && Utils.isThenable(vbInitConfig.DIGEST_PROMISE)) {
        this.fetchPartialManifestPromise = vbInitConfig.DIGEST_PROMISE.then((response) => {
          if (!response.ok) {
            throw new Error('Response not OK when fetching partial extension manifest');
          }

          this.partialDigestUrl = response.url || null;
          return response.json().then(({ digest }) => [
            Promise.resolve(EndpointExtensionAdapter.readRuntimeDigest(digest[Constants.Digest.RUNTIME])),
            Promise.resolve(EndpointExtensionAdapter.readRequirejsInfoDigest(digest[Constants.Digest.REQUIREJS_INFO])),
            Promise.resolve(digest[Constants.Digest.APP_UI_INFO]),
          ]);
        });
        // Calculate the App UI name, that will be use in getDigestPathsForCaching
        // to calculate the path of the partial digest URL for caching.
        const { APP_NAME } = vbInitConfig;
        if (APP_NAME) {
          // The following logic is to retrieve the App UI name, not the AppName.
          // The App UI is the segment immediately following the appName.
          const { pathname } = window.location;
          const appNameIndex = pathname.indexOf(APP_NAME);
          const appUrl = appNameIndex >= 0 && pathname.substring(appNameIndex);
          if (appUrl) {
            [, this.partialDigestAppUi] = appUrl.split('/');
          }
        }
      } else {
        // Update this property since its value is used later to log the type of digest to the console
        this.digestState = Constants.DigestState.FULL;
      }

      // Preference is given to the DIGEST_ENDPOINT property set when using SUIS
      this.url = Utils.cleanString(vbInitConfig.DIGEST_ENDPOINT);

      if (this.url) {
        this.digestLoader = new EndpointExtensionAdapter(this.url);
      }

      // If it's not using DIGEST_ENDPOINT, it might be using REGISTRY_URL
      if (!this.url) {
        this.url = Utils.cleanString(vbInitConfig.REGISTRY_URL);

        if (this.url) {
          if (this.url.indexOf('http') === 0) {
            this.digestLoader = new RemoteExtensionAdapter(this.url);
          } else {
            this.localAdapter = 'vb/private/vx/localExtensionAdapter';
          }
        }
      }

      // For DIGEST_ENDPOINT and REGISTRY_URL case, we can initiate the fetch immediately
      if (this.digestLoader) {
        // Only store the promise, don't block on it.
        if (this.fetchPartialManifestPromise) {
          this.loadPartialManifest = (appUiUrlId) => this.fetchPartialManifestPromise.then(() => {
            if (!this.partialDigestUrl) {
              throw new Error('Bootstrap partial digest response does not provide a URL for later App UI navigation.');
            }

            const partialDigestUrl = new URL(this.partialDigestUrl);
            if (!partialDigestUrl.searchParams.has('appRoute')) {
              throw new Error('Bootstrap partial digest URL does not define the appRoute parameter.');
            }
            partialDigestUrl.searchParams.set('appRoute', appUiUrlId);
            return this.digestLoader.loadPartialDigest(partialDigestUrl.href);
          });

          // In partial digest mode the runtime and requirejs metadata are loaded per App UI.
          // The full app-ui-info digest is still fetched once upfront for global App UI discovery.
          const appUiInfoDigestPromise = this.digestLoader.loadDigests([Constants.Digest.APP_UI_INFO])[0];
          this.fetchFullManifestArray = [
            Promise.resolve([]),
            Promise.resolve([]),
            appUiInfoDigestPromise,
          ];

          // Missing-provider recovery needs only the runtime and requirejs metadata. Reuse the full
          // app-ui-info digest fetched above instead of requesting it again as part of a full manifest.
          this.loadFullManifest = (options) => {
            const types = [Constants.Digest.RUNTIME, Constants.Digest.REQUIREJS_INFO];
            // Missing-provider recovery must know when either digest failed. The bootstrap fallback has no
            // recovery requirement, so it keeps EndpointExtensionAdapter's normal best-effort behavior.
            const fullManifestDigests = options && options.rejectOnError
              ? this.digestLoader.loadDigests(types, options)
              : this.digestLoader.loadDigests(types);
            const [runtimeDigestPromise, requirejsInfoDigestPromise] = fullManifestDigests;
            return [runtimeDigestPromise, requirejsInfoDigestPromise, appUiInfoDigestPromise];
          };
        } else {
          this.fetchFullManifestArray = this.digestLoader.initiateFetchManifest();
        }
      }
    }

    async init() {
      // Temporary support for local adapter only in full digest mode.
      if (!this.digestLoader && this.url && this.localAdapter) {
        const Adapter = await Utils.getResource(this.localAdapter);
        this.digestLoader = new Adapter(this.url);
        // Only store the promise, don't block on it.
        this.fetchFullManifestArray = this.digestLoader.initiateFetchManifest();
      }
    }

    /**
     * @return {ExtensionRegistry|PartialDigestExtensionRegistry|FullDigestExtensionRegistry}
     */
    createExtensionRegistry() {
      if (!this.hasRegistry()) {
        return new ExtensionRegistry(this);
      }

      const ExtensionRegistryClass = this.extensionRegistryModule
        || (this.digestState === Constants.DigestState.PARTIAL && this.fetchPartialManifestPromise
          ? PartialDigestExtensionRegistry
          : FullDigestExtensionRegistry);

      return new ExtensionRegistryClass(this);
    }

    /**
     * Return true if the application is using a registry
     * @return {Boolean}
     */
    hasRegistry() {
      return !!this.digestLoader;
    }

    /**
     * Retrieve the cache info for digests
     * Return array of resource info which the cache name, path and array or resources under that path
     * This is called by pwaServiceWorkerManagerClass for caching
     * @return {Array<Object>}
     */
    getDigestPathsForCaching() {
      const cachingInfo = [];
      // Don't bother with LocalExtensionAdapter!
      if (this.url.startsWith('http')) {
        const path = this.digestLoader.getUrl().href;

        // partialDigestUrl is set only after the bootstrap partial digest succeeds. A rejected bootstrap request
        // falls back to the full manifest, so its runtime and RequireJS digests must be cached for offline reload.
        const hasBootstrapPartialDigest = !!this.partialDigestUrl;
        const digests = hasBootstrapPartialDigest
          ? [Constants.Digest.APP_UI_INFO, Constants.Digest.PWA_INFO]
          : [
            Constants.Digest.RUNTIME,
            Constants.Digest.REQUIREJS_INFO,
            Constants.Digest.APP_UI_INFO,
            Constants.Digest.PWA_INFO,
          ];

        const resources = digests.map((digest) => `?type=${digest}`);

        cachingInfo.push({ name: 'vb-digest', path, resources });

        // Cache the original bootstrap partial URL without inferring an endpoint shape from the full-digest URL.
        if (hasBootstrapPartialDigest && this.partialDigestAppUi) {
          const partialDigestUrl = new URL(this.partialDigestUrl);
          partialDigestUrl.searchParams.set('appRoute', this.partialDigestAppUi);

          cachingInfo.push({
            // App Ui is part of the name because each App Ui have a different digest
            name: `vb-partialDigest-${this.partialDigestAppUi}`,
            path: null, // no path, only one entry which is the resource URL
            resources: [partialDigestUrl.href],
          });
        }
      }

      return cachingInfo;
    }
  }

  return RegistryConfig;
});

